import React from 'react';
import { TableRow, TableCell, Box, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import { Visibility as ViewIcon, Description as QueryIcon } from '@mui/icons-material';
import { colors, fonts, spacing } from '../../constants';
import { getDisplayStatus, formatQueryType } from '../../utils/escalatedQueriesUtils';
import { formatDateTime } from '../../utils/dateUtils';

/**
 * EscalatedQueryTableRow Component
 * Individual query row in table
 */
const EscalatedQueryTableRow = ({
  query,
  onView,
  tableBodyCellStyles,
  tableRowStyles,
}) => {
  const statusInfo = getDisplayStatus(query);

  return (
    <TableRow
      key={query._id}
      hover
      sx={{
        ...tableRowStyles,
        backgroundColor: '#ffffff',
        transition: 'all 0.2s ease',
        borderLeft: `2px solid transparent`,
        borderTop: `1px solid ${colors.borderDefault}`,
        '& .MuiTableCell-root': {
          borderTop: `1px solid ${colors.borderDefault} !important`,
          borderBottom: `1px solid ${colors.borderDefault} !important`,
        },
        '&:hover': {
          backgroundColor: '#fafbfc',
          borderLeft: `2px solid #0e172a`,
          borderTop: `1px solid ${colors.borderDefault}`,
          transform: 'translateX(2px)',
          '& .MuiTableCell-root': {
            borderTop: `1px solid ${colors.borderDefault} !important`,
            borderBottom: `1px solid ${colors.borderDefault} !important`,
          },
        },
      }}
    >
      <TableCell sx={{ ...tableBodyCellStyles, pl: spacing.lg, py: spacing.sm, borderBottom: `1px solid ${colors.borderDefault} !important`, borderTop: `1px solid ${colors.borderDefault} !important` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
          <QueryIcon sx={{ fontSize: '14px', color: '#0e172a', opacity: 0.7 }} />
          <Typography sx={{ fontWeight: fonts.weightMedium, fontSize: '0.8125rem', color: '#1f2937' }}>
            {formatQueryType(query.queryType)}
          </Typography>
        </Box>
      </TableCell>
      <TableCell sx={{ ...tableBodyCellStyles, py: spacing.sm, borderBottom: `1px solid ${colors.borderDefault} !important`, borderTop: `1px solid ${colors.borderDefault} !important` }}>
        <Typography 
          sx={{ 
            fontWeight: fonts.weightMedium,
            color: '#1f2937',
            fontFamily: 'monospace',
            fontSize: '0.8125rem',
          }}
        >
          {query.queryId || query._id}
        </Typography>
      </TableCell>
      <TableCell sx={{ ...tableBodyCellStyles, py: spacing.sm, borderBottom: `1px solid ${colors.borderDefault} !important`, borderTop: `1px solid ${colors.borderDefault} !important` }}>
        {statusInfo ? (
          <Chip
            label={statusInfo.label}
            size="small"
            sx={{
              backgroundColor: `${statusInfo.color}1a`,
              color: statusInfo.color,
              fontWeight: fonts.weightSemiBold,
              fontSize: '0.7rem',
              alignSelf: 'flex-start',
            }}
          />
        ) : null}
      </TableCell>
      <TableCell sx={{ ...tableBodyCellStyles, py: spacing.sm, borderBottom: `1px solid ${colors.borderDefault} !important`, borderTop: `1px solid ${colors.borderDefault} !important` }}>
        <Typography sx={{ fontWeight: fonts.weightMedium, fontSize: '0.8125rem', color: '#1f2937' }}>
          {query.raisedBy?.name || 'N/A'}
        </Typography>
      </TableCell>
      <TableCell sx={{ ...tableBodyCellStyles, py: spacing.sm, borderBottom: `1px solid ${colors.borderDefault} !important`, borderTop: `1px solid ${colors.borderDefault} !important` }}>
        <Typography sx={{ fontWeight: fonts.weightMedium, fontSize: '0.8125rem', color: '#1f2937' }}>
          {query.escalatedTo?.name || query.assignee?.name || 'N/A'}
        </Typography>
      </TableCell>
      <TableCell sx={{ ...tableBodyCellStyles, py: spacing.sm, borderBottom: `1px solid ${colors.borderDefault} !important`, borderTop: `1px solid ${colors.borderDefault} !important` }}>
        <Typography 
          sx={{ 
            fontSize: '0.8125rem',
            color: '#6b7280',
          }}
        >
          {formatDateTime(query.raisedAt)}
        </Typography>
      </TableCell>
      <TableCell sx={{ ...tableBodyCellStyles, py: spacing.sm, borderBottom: `1px solid ${colors.borderDefault} !important`, borderTop: `1px solid ${colors.borderDefault} !important` }}>
        <Typography 
          sx={{ 
            fontSize: '0.8125rem',
            color: '#6b7280',
          }}
        >
          {formatDateTime(query.slaDueDate)}
        </Typography>
      </TableCell>
      <TableCell sx={{ ...tableBodyCellStyles, textAlign: 'right', width: '120px', minWidth: '120px', py: spacing.sm, borderBottom: `1px solid ${colors.borderDefault} !important`, borderTop: `1px solid ${colors.borderDefault} !important` }}>
        <Box sx={{ display: 'flex', gap: spacing.xs, alignItems: 'center', justifyContent: 'flex-end' }}>
          <Tooltip title="View Details (Read Only)">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onView(query);
              }}
              sx={{
                color: '#0e172a',
                '&:hover': { backgroundColor: colors.primaryTintHover },
              }}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
};

export default EscalatedQueryTableRow;
